import { FC, useContext, useMemo } from 'react'
import { useUA } from 'context'
import gql from 'graphql-tag'
import { isEmpty, omitBy } from 'lodash'
import styled, { css } from 'styled-components'
import {
  useRecruitListQuery,
  ActivityStatus,
  ActivityFilter,
} from 'generated/graphql'
import useJobCategoryStore from '@app.feature/jobCategory/store/store.jobCategory'
import { ActivityTypeID } from 'constants/enums'
import { RecruitListScreenContext } from '../../context'
import useRecruitFilterSyncToUrlQuery from '../../hook/useRecruitFilterSyncToUrlQuery'
import RecruitListOrderBy from './component/RecruitListOrderBy'
import RecruitListPagination from './component/RecruitListPagination'
import ListTableDesktop from './component/desktop/ListTable.desktop'
import ListTableMobile from './component/mobile/ListTable.mobile'

gql`
  query RecruitList(
    $filterBy: ActivityFilter
    $pageSize: Int!
    $page: Int!
    $activityOrder: ActivityOrder
  ) {
    currentUser {
      ...RecruitListItem_currentUser
    }
    activities(
      filterBy: $filterBy
      pagination: { page: $page, pageSize: $pageSize }
      orderBy: $activityOrder
    ) {
      totalCount
      nodes {
        id
        ...RecruitListItem_activity
      }
    }
    fallbackActivities: activities(
      filterBy: $filterBy
      pagination: { page: 1, pageSize: 4 }
      orderBy: { direction: DESC, field: SCRAP_COUNT }
    ) {
      totalCount
      nodes {
        id
        ...RecruitListItem_activity
      }
    }
  }
`

const PAGE_SIZE = 20

interface RecruitListProps {
  activityType: ActivityTypeID
  fixedFilterBy?: ActivityFilter
}

const RecruitListTable: FC<RecruitListProps> = ({
  activityType,
  fixedFilterBy,
}) => {
  const { isMobile } = useUA()

  const { state } = useContext(RecruitListScreenContext)
  const filters = omitBy(state.filterBy, isEmpty)

  const jobCategoryStore = useJobCategoryStore()

  const variables = useMemo(() => {
    const result = {
      filterBy: {
        status: ActivityStatus.OPEN,
        activityTypeID: String(ActivityTypeID.RECRUIT),
        ...filters,
        ...fixedFilterBy,
        ...(!isMobile && {
          categoryIDs: jobCategoryStore.showSelectedIds,
        }),
      },
      activityOrder: {
        field: state.orderBy.field,
        direction: state.orderBy.direction,
      },
      page: state.page,
      pageSize: PAGE_SIZE,
    }

    return result
  }, [
    filters,
    fixedFilterBy,
    isMobile,
    jobCategoryStore.showSelectedIds,
    state,
  ])

  const { data, loading, error } = useRecruitListQuery({
    notifyOnNetworkStatusChange: true,
    variables,
  })

  const totalCount = data?.activities?.totalCount || 0

  useRecruitFilterSyncToUrlQuery(variables)

  return (
    <StyledWrapper data-testid="activity-list-screen">
      <RecruitListOrderBy activityConnection={data?.activities} />
      {isMobile && (
        <ListTableMobile
          activityType={activityType}
          loading={loading}
          data={data}
          error={error}
          filters={filters}
          currentOrderByField={state.orderBy.field}
        />
      )}
      {!isMobile && (
        <ListTableDesktop
          activityType={activityType}
          loading={loading}
          data={data}
          error={error}
          filters={filters}
          currentOrderByField={state.orderBy.field}
        />
      )}
      <RecruitListPagination totalCount={totalCount} />
    </StyledWrapper>
  )
}

export default RecruitListTable

const StyledWrapper = styled.section`
  ${({ theme }) => css`
    margin-top: ${theme.isMobile ? '15px' : '37px'};
  `}
`
